import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from './skeleton';

export interface DataTableColumn<Row> {
  key: string;
  header: ReactNode;
  /** Optional label for the stacked mobile representation. */
  mobileHeader?: ReactNode;
  align?: 'start' | 'end' | 'center';
  width?: string;
  /** Column hidden below this breakpoint. */
  hideBelow?: 'sm' | 'md' | 'lg';
  cell: (row: Row) => ReactNode;
  /** Header content when the page owns sorting (chevrons etc.). */
  headerCell?: ReactNode;
  /** Accessible sort state for a page-owned sortable header. */
  ariaSort?: 'ascending' | 'descending' | 'none';
}

const HIDE = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
} as const;

const ALIGN = { start: 'text-start', end: 'text-end', center: 'text-center' } as const;

/**
 * DataTable shell (DESIGN.md §5): micro-label headers, 44px rows, hairlines,
 * hover surface. Sorting, selection and pagination stay page-owned.
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  caption,
  empty,
  loading,
  loadingRows = 5,
  onRowClick,
  rowClassName,
  className,
  dense,
  responsive = 'table',
}: {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string | number;
  caption?: string;
  empty?: ReactNode;
  loading?: boolean;
  loadingRows?: number;
  onRowClick?: (row: Row) => void;
  rowClassName?: (row: Row) => string | undefined;
  className?: string;
  dense?: boolean;
  /** Render rows as readable key/value cards until the desktop table has room. */
  responsive?: 'table' | 'stacked';
}) {
  const rowH = dense ? 'h-10' : 'h-11';
  const stacked = responsive === 'stacked';
  const mobileColumns = columns.filter((column) => column.key !== 'selection');

  const renderMobileRow = (row: Row) => (
    <article key={rowKey(row)} className="border-b border-line p-4 last:border-0">
      <div className="space-y-2.5">
        {mobileColumns.map((column, index) => (
          <div
            key={column.key}
            className={cn(
              'grid min-w-0 grid-cols-[minmax(5.5rem,38%)_minmax(0,1fr)] items-start gap-3',
              index === 0 && 'pb-1'
            )}
          >
            <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-3">
              {column.mobileHeader ?? column.header}
            </span>
            <div className="min-w-0 text-end text-sm text-ink">{column.cell(row)}</div>
          </div>
        ))}
      </div>
    </article>
  );

  return (
    <div
      className={cn(
        'w-full max-w-full min-w-0 rounded-card border border-line bg-surface',
        stacked ? 'overflow-visible lg:overflow-x-auto' : 'overflow-x-auto',
        className
      )}
    >
      {stacked ? (
        <div className="lg:hidden">
          {loading
            ? Array.from({ length: loadingRows }).map((_, index) => (
                <div key={`mobile-s-${index}`} className="border-b border-line p-4 last:border-0">
                  <div className="space-y-3">
                    <Skeleton className="h-3.5 w-2/3" />
                    <Skeleton className="h-3.5 w-1/2" />
                    <Skeleton className="h-3.5 w-3/5" />
                  </div>
                </div>
              ))
            : rows.map(renderMobileRow)}
          {!loading && rows.length === 0 ? (
            <div className="px-4 py-10 text-center text-[13px] text-ink-2">
              {empty ?? 'Nothing here yet.'}
            </div>
          ) : null}
        </div>
      ) : null}

      <table
        className={cn('w-full min-w-[640px] border-collapse text-sm', stacked && 'hidden lg:table')}
      >
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr className="border-b border-line">
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                aria-sort={c.ariaSort}
                style={c.width ? { width: c.width } : undefined}
                className={cn(
                  'h-9 px-3 text-[11px] font-medium uppercase tracking-[0.08em] text-ink-3 first:ps-4 last:pe-4',
                  ALIGN[c.align ?? 'start'],
                  c.hideBelow && HIDE[c.hideBelow]
                )}
              >
                {c.headerCell ?? c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: loadingRows }).map((_, i) => (
                <tr key={`s-${i}`} className={cn('border-b border-line last:border-0', rowH)}>
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={cn('px-3 first:ps-4 last:pe-4', c.hideBelow && HIDE[c.hideBelow])}
                    >
                      <Skeleton className="h-3.5 w-[60%]" />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    'border-b border-line transition-colors duration-100 last:border-0',
                    rowH,
                    onRowClick && 'cursor-pointer hover:bg-surface-2',
                    !onRowClick && 'hover:bg-surface-2/60',
                    rowClassName?.(row)
                  )}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={cn(
                        'px-3 align-middle text-ink first:ps-4 last:pe-4',
                        ALIGN[c.align ?? 'start'],
                        c.hideBelow && HIDE[c.hideBelow]
                      )}
                    >
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              ))}
          {!loading && rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-10 text-center text-[13px] text-ink-2"
              >
                {empty ?? 'Nothing here yet.'}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
